import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { PostMessageEvents, Widget } from '@xsolla/login-sdk';
import { Grid, Notification } from '@xsolla/ui-core';
import { AlertDangerColorful, SuccessColorful } from '@xsolla/ui-icons';

import { BookIcon, QuestionIcon } from '../../../assets';
import { PROJECT_LINKS } from '../../../constants';
import { useConfig } from '../../../contexts/config';
import { usePaths } from '../../../routes/routesHelper';
import getTryConnectionLink from '../../Authentication/SocialsList/SocialNetworkCard/utils';
import { CustomModal, CustomWidgetIcon, HumanIcon } from '../../ui';
import { FeatureCard, SupportLink } from './components';
import {
  DivWithHeight,
  FooterWrapper,
  ResultTitleWrapper,
  ResultWrapper,
  Subtitle,
  Title
} from './styles';
import {
  TestWidgetModalProps,
  WidgetDimensions,
  WidgetError,
  WidgetErrorMessage,
  WidgetSocialMessage
} from './types';

const TestWidgetModal = ({ onClose, projectId }: TestWidgetModalProps): JSX.Element => {
  const { customizeWidgetPath, usersPath } = usePaths();
  const intl = useIntl();
  const { locale, xsollaAnalytics, merchantId } = useConfig();

  const [widgetHeight, setWidgetHeight] = useState(450);
  const [authComplete, setAuthComplete] = useState(false);
  const [authError, setAuthError] = useState<WidgetError | null>(null);

  useEffect(() => {
    xsollaAnalytics.sendOpen('login_pa_module__test_login_widget', 'open');
  }, []);

  useEffect(() => {
    const widget = new Widget({
      projectId,
      url: process.env.LOGIN_WIDGET_URL || 'https://login-widget.xsolla.com/latest',
      preferredLocale: locale === 'zh' ? 'zh_CN' : `${locale}_XX`,
      enablePostMessageLogin: true,
      disableCache: true
    });

    widget.on(PostMessageEvents.LoginSuccess, onWidgetSuccess);
    widget.on(PostMessageEvents.SignupSuccess, onWidgetSuccess);

    widget.on(PostMessageEvents.LoginError, onWidgetError);
    widget.on(PostMessageEvents.SignupError, onWidgetError);

    widget.on(PostMessageEvents.SocialLogin, onWidgetSocial);

    widget.on(PostMessageEvents.Dimensions, ({ dimensions }: WidgetDimensions) => {
      setWidgetHeight(dimensions.height + 50);
    });

    widget.mount('test-widget');

    return () => {
      widget.removeAllListeners();
    };
  }, []);

  const onWidgetSuccess = useCallback(() => {
    setAuthComplete(true);
  }, []);

  const onWidgetError = useCallback(({ error }: WidgetErrorMessage) => {
    setAuthError(error);
    setAuthComplete(true);
  }, []);

  const onWidgetSocial = useCallback(({ socialName }: WidgetSocialMessage) => {
    window.open(getTryConnectionLink({ projectId, locale, socialName, merchantId }), '_blank');
    setAuthComplete(true);
  }, []);

  const renderFooter = useMemo(() => {
    return (
      <FooterWrapper>
        {!authError && (
          <>
            <Subtitle>{intl.formatMessage({ id: 'try-login.success.features.label' })}</Subtitle>
            <Grid cols={2} gap={18} allowOverflow>
              <FeatureCard
                icon={<HumanIcon height={24} width={24} />}
                text={intl.formatMessage({ id: 'navigation.settings.user-db.users.title' })}
                link={usersPath(projectId)}
                onClose={onClose}
              />
              <FeatureCard
                icon={<CustomWidgetIcon height={24} width={24} />}
                text={intl.formatMessage({
                  id: 'navigation.settings.customization.widget-customization.title'
                })}
                link={customizeWidgetPath(projectId)}
                onClose={onClose}
              />
            </Grid>
          </>
        )}
        <Subtitle>{intl.formatMessage({ id: 'try-login.success.support.label' })}</Subtitle>
        <SupportLink
          icon={<BookIcon />}
          text={intl.formatMessage({ id: 'try-login.success.support.integration' })}
          link={PROJECT_LINKS.DOCUMENTATION}
        />
        <SupportLink
          icon={<QuestionIcon />}
          text={intl.formatMessage({ id: 'try-login.success.support.help' })}
          link={PROJECT_LINKS.PARTNER_SUPPORT}
        />
      </FooterWrapper>
    );
  }, [authError]);

  const renderAuthResult = useMemo(() => {
    if (authError) {
      return (
        <ResultWrapper>
          <ResultTitleWrapper>
            <AlertDangerColorful size={40} />
            <Title>{intl.formatMessage({ id: 'try-login.error.title' })}</Title>

            <Notification caption={authError.description} palette="danger">
              {intl.formatMessage({ id: 'try-login.error.text' }, { code: authError.code })}
            </Notification>
          </ResultTitleWrapper>

          {renderFooter}
        </ResultWrapper>
      );
    }

    return (
      <ResultWrapper>
        <ResultTitleWrapper>
          <SuccessColorful size={40} />
          <Title>{intl.formatMessage({ id: 'try-login.success.title' })}</Title>
        </ResultTitleWrapper>

        {renderFooter}
      </ResultWrapper>
    );
  }, [authError]);

  return (
    <CustomModal onClose={onClose}>
      {authComplete ? (
        renderAuthResult
      ) : (
        <DivWithHeight height={widgetHeight || 450} id="test-widget" />
      )}
    </CustomModal>
  );
};

export default TestWidgetModal;
